import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LedgerUIProvider, type LedgerUIConfig } from "@tanvishtech/ledger-ui";

import { useAuthStore } from "../store/authStore";
import { api } from "../api/client";
import { formatMoney } from "./formatters";
import { Card } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { Badge } from "../components/ui/Badge";

// The one place this app's specifics meet the reusable accounting pages
// (@tanvishtech/ledger-ui): API client, translations, permission rule, money
// formatting, and the design system are all injected here — the package
// itself imports none of them (extraction plan, Phase 5).
export function LedgerUIBridge({ children }: { children: ReactNode }) {
	const { t } = useTranslation();
	const currentBusiness = useAuthStore((s) => s.currentBusiness);
	const user = useAuthStore((s) => s.user);
	const permissions = useAuthStore((s) => s.permissions);

	// The ledger stamps each entry with the business's currency (#25,
	// EntryCurrency), so the display currency is read from the same place. The
	// outlet's own column is no longer on its form and is kept only until it is
	// dropped: a new outlet gets INR whatever the business trades in. Undefined
	// falls back to rupees inside formatMoney.
	const currency = currentBusiness?.currency || undefined;

	const config: LedgerUIConfig = {
		api,
		t,
		// Owner/admin bypass is THIS app's rule, so it lives here, not in the
		// pages.
		can: (permission) =>
			user?.org_role === "owner" || user?.org_role === "admin" || permissions.includes(permission),
		formatMoney: (amount) => formatMoney(amount, currency),
		tenantId: currentBusiness?.id ?? null,
		components: { Card, Spinner, Button, Input, Modal, Badge },
	};

	return <LedgerUIProvider config={config}>{children}</LedgerUIProvider>;
}
